import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDashboardPage, SpecraDataTable, SpecraEmptyState, SpecraInlineBanner, SpecraLink, SpecraMetricStrip, SpecraOverlayDrawer, SpecraPageHeader, SpecraRecordRail, SpecraReferenceRow, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProposalProject } from '../../../../../hooks/useProposalProject';
import { proposalHref } from '../../../../../lib/proposalHref';
import { renderRouterLink } from '../../../../../lib/renderRouterLink';
import { proposalLoadErrorMessage } from '../../pages/proposalLoadError';
import { CarriedInTable } from '../CarriedInTable';
import { MilestonesPanel } from '../MilestonesPanel';
import { headerPills, headerSummary, toMetrics, toPassFailRows, toRailSections, toSectionRows } from './proposalHomeView';
import { useMyProposalSections, useProposalHome } from './useProposalHome';
const T = Testids.proposalHomeV3;
const METRIC_TESTID = {
    compliance: T.kpiCompliance,
    drafting: T.kpiDrafting,
    price: T.kpiPrice,
    review: T.kpiReview
};
const SECTION_COLUMNS = [
    {
        id: 'volume',
        header: 'Volume',
        cell: (r)=>r.volume || '—'
    },
    {
        id: 'section',
        header: 'Section',
        cell: (r)=>/*#__PURE__*/ _jsx(SpecraLink, {
                href: r.href,
                renderLink: renderRouterLink,
                children: r.title
            })
    },
    {
        id: 'due',
        header: 'Due',
        cell: (r)=>r.due
    }
];
export function ProposalHomeV3({ projectId }) {
    const navigate = useNavigate();
    const project = useProposalProject(projectId);
    const home = useProposalHome(projectId);
    const mine = useMyProposalSections(projectId);
    const [milestonesOpen, setMilestonesOpen] = useState(false);
    const name = project.data?.project.name;
    const title = `${name ?? 'Proposal'}${name ? ' · Proposal' : ''}`;
    // Recheck authority on every visit: never render cached figures for a failed or in-flight read.
    const loading = home.isPending || home.isFetching || !home.isFetchedAfterMount;
    const data = home.isError || loading ? undefined : home.data;
    const caps = data?.capabilities;
    const pills = data ? headerPills(data.header) : [];
    const header = /*#__PURE__*/ _jsx(SpecraPageHeader, {
        titleSize: "home",
        title: title,
        meta: data ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                pills.map((p)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: p.variant,
                        children: p.text
                    }, p.id)),
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: headerSummary(data.header)
                })
            ]
        }) : undefined,
        actions: // Export (PRP-35 ExportPackageDialog / can_export_package) is not on this branch.
        data && caps?.canStartReview ? /*#__PURE__*/ _jsx(SpecraButton, {
            variant: "default",
            "data-testid": T.startReview,
            onClick: ()=>{
                const milestone = data.nextReview?.milestoneId;
                navigate(`${proposalHref(projectId, 'reviews')}?new=1${milestone ? `&milestone=${encodeURIComponent(milestone)}` : ''}`);
            },
            children: "Start review"
        }) : undefined
    });
    const railData = data ? toRailSections(data) : [];
    const canEdit = !!caps?.canEditProposal;
    if (data && canEdit && !railData.some((s)=>s.id === 'milestones')) {
        railData.push({
            id: 'milestones',
            title: 'Milestones',
            facts: []
        });
    }
    const rail = railData.map((s)=>({
            id: s.id,
            title: s.title,
            action: s.id === 'milestones' && canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "ghost",
                size: "xs",
                onClick: ()=>setMilestonesOpen(true),
                children: "Edit"
            }) : undefined,
            facts: s.facts.map((f)=>({
                    id: f.id,
                    label: f.label,
                    value: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            f.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                                href: f.href,
                                renderLink: renderRouterLink,
                                children: f.value
                            }) : f.value,
                            f.suffix ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "span",
                                tone: "success",
                                children: f.suffix
                            }) : null
                        ]
                    })
                }))
        }));
    const passFail = data ? toPassFailRows(data.passFailOpen) : [];
    const sectionRows = mine.data ? toSectionRows(mine.data.actions) : [];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
                "data-testid": T.page,
                padding: "page",
                railVariant: "record",
                header: header,
                rail: rail.length > 0 ? /*#__PURE__*/ _jsx(SpecraRecordRail, {
                    "data-testid": T.rail,
                    sections: rail
                }) : undefined,
                children: [
                    home.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "danger",
                        action: /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: ()=>home.refetch(),
                            children: "Retry"
                        }),
                        children: proposalLoadErrorMessage(home.error)
                    }) : /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                        "data-testid": T.kpi,
                        density: "compact",
                        loading: loading,
                        metrics: data ? toMetrics(data, projectId) : [],
                        getItemTestId: (m)=>METRIC_TESTID[m.id],
                        renderLink: renderRouterLink
                    }),
                    data && data.carriedIn.length > 0 && /*#__PURE__*/ _jsxs("section", {
                        "data-testid": T.carriedIn,
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "Carried in from the pursuit",
                                action: /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    children: "Nothing re-typed: each item links back to where it came from"
                                })
                            }),
                            /*#__PURE__*/ _jsx(CarriedInTable, {
                                rows: data.carriedIn
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        "data-testid": T.mySections,
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "My sections"
                            }),
                            !mine.isLoading && !mine.isError && sectionRows.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                title: "No sections assigned to you"
                            }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                                caption: "My sections",
                                columns: SECTION_COLUMNS,
                                rows: sectionRows,
                                getRowId: (r)=>r.id,
                                loading: mine.isLoading || loading
                            })
                        ]
                    }),
                    passFail.length > 0 && /*#__PURE__*/ _jsxs("section", {
                        "data-testid": T.passFail,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: `Pass / fail requirements still open (${passFail.length})`
                            }),
                            passFail.map((r)=>/*#__PURE__*/ _jsx(SpecraReferenceRow, {
                                    "data-testid": T.passFailRow,
                                    reference: r.reference,
                                    label: r.label,
                                    href: r.href,
                                    renderLink: renderRouterLink,
                                    status: /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: r.status === 'Partial' ? 'warning' : 'danger',
                                        children: r.status
                                    })
                                }, r.id))
                        ]
                    })
                ]
            }),
            canEdit && /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: milestonesOpen,
                onOpenChange: setMilestonesOpen,
                title: "Milestones",
                width: "lg",
                children: /*#__PURE__*/ _jsx(MilestonesPanel, {
                    projectId: projectId
                })
            })
        ]
    });
}
